import { useEffect, useState, type FormEvent } from "react";
import { useSettings } from "../hooks/useSettings";
import type { Book, CreateBookOptions, PaperSize } from "../api/types";
import Modal from "./Modal";

const PAPER_SIZES: PaperSize[] = ["A5", "A4", "Letter"];

interface RegenerateDialogProps {
  open: boolean;
  book: Book;
  pending?: boolean;
  onClose: () => void;
  onConfirm: (options: CreateBookOptions) => void;
}

export default function RegenerateDialog({ open, book, pending, onClose, onConfirm }: RegenerateDialogProps) {
  const { data: settings } = useSettings();
  const [paperSize, setPaperSize] = useState<PaperSize>("A5");
  const [force, setForce] = useState(false);

  useEffect(() => {
    if (open) {
      setPaperSize(settings?.paper_size ?? "A5");
      setForce(false);
    }
  }, [open, settings?.paper_size]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    onConfirm({ paper_size: paperSize, force });
  }

  const title = book.book_title ?? book.video_title ?? "this book";

  return (
    <Modal open={open} onClose={onClose} title="Regenerate book">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-muted">
          Re-runs the pipeline for “{title}”. This replaces the current chapters, EPUB, and PDF.
        </p>

        <div>
          <label htmlFor="regen-paper" className="mb-1 block text-sm font-medium text-text">
            Paper size
          </label>
          <select
            id="regen-paper"
            value={paperSize}
            onChange={(e) => setPaperSize(e.target.value as PaperSize)}
            className="w-full rounded-md border border-border bg-bg pl-3 pr-9 py-2 text-sm text-text"
          >
            {PAPER_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2 text-sm text-text">
          <input
            type="checkbox"
            checked={force}
            onChange={(e) => setForce(e.target.checked)}
            className="h-4 w-4 rounded border-border text-accent"
          />
          Force full rebuild (ignore existing checkpoints)
        </label>

        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium text-text hover:bg-bg"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={pending}
            className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-strong disabled:opacity-50"
          >
            {pending ? "Starting…" : "Regenerate"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
